import { Divider, Stack, styled, Typography } from '@mui/material'
import { ActiveSession } from '@weblite/ts-action/auth'
import { Fragment, useCallback, useState } from 'react'
import { SettingsSection } from '$common/settings-section'
import { ListItemCriticalTextButton } from '$common/buttons'
import { ListItemTextButton } from '$common/buttons/list-item-text-button'
import { showRemoveMenu } from '$comp/modals/menus/remove-menu'
import { showTerminateSessionsModal } from '$comp/modals/confirmation-dialog'
import { useActiveSessions } from '$logic/auth/react-hook'
import { SESSIONS_PAGE_SIZE } from '$logic/auth/constants'
import { SessionItem } from './session-item'
import { t } from '$i18n'

const InfixDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

interface SessionsListProps {
  showCurrentDevice?: boolean
  onTerminateOtherSessions?: () => void
  onTerminateSession?: (selector: string) => void
}

export const SessionsList = ({
  showCurrentDevice = true,
  onTerminateOtherSessions,
  onTerminateSession,
}: SessionsListProps) => {
  const {
    value: activeSessions,
    loading = false,
    terminateOtherSessions,
    terminateSession,
  } = useActiveSessions()

  // `activeSessions` is 'TOO_MANY_REQUESTS' when the request was throttled and
  // `null` when it failed. Destructuring either yields undefined everywhere,
  // which used to render a silently blank page.
  const failure =
    activeSessions === 'TOO_MANY_REQUESTS'
      ? t('activeSessions.tooManyRequests')
      : activeSessions === null
      ? t('activeSessions.loadFailed')
      : undefined

  const sessions = failure ? undefined : activeSessions
  const { current, others } = sessions || {}

  const handleTerminateOtherSessions = useCallback(() => {
    showTerminateSessionsModal(() =>
      terminateOtherSessions().then(() => {
        if (onTerminateOtherSessions) {
          onTerminateOtherSessions()
        }
      }),
    )
  }, [terminateOtherSessions, onTerminateOtherSessions])

  const handleTerminateSession = useCallback(
    (selector: string) => {
      showRemoveMenu({
        onRemove: () =>
          terminateSession(selector).then(() => {
            if (onTerminateSession) {
              onTerminateSession(selector)
            }
          }),
      })
    },
    [terminateSession, onTerminateSession],
  )

  if (loading) {
    return null // TODO: Implement skeleton
  }

  if (failure) {
    return (
      <Stack sx={{ width: '100%', p: 2.5 }}>
        <Typography variant="body2" color="neutral600" textAlign="center">
          {failure}
        </Typography>
      </Stack>
    )
  }

  if (!sessions) {
    return null
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {showCurrentDevice && current && (
        <SettingsSection headerText={t('activeSessions.currentDevice')}>
          <>
            <SessionItem
              ip={current.identity?.ip}
              userAgent={current.identity?.userAgent}
              date={current.updatedAt}
            />
            <Divider />
            <ListItemCriticalTextButton
              text={t('activeSessions.deactivateOtherDevices')}
              onClick={handleTerminateOtherSessions}
            />
          </>
        </SettingsSection>
      )}

      {others && others.length > 0 && (
        <SettingsSection headerText={t('activeSessions.otherActiveDevices')}>
          <OtherSessions
            sessions={others}
            onTerminate={handleTerminateSession}
          />
        </SettingsSection>
      )}
    </Stack>
  )
}

/**
 * Renders the list in pages. A user who signs in often can accumulate hundreds
 * of sessions, and rendering them all at once locks up low-end devices.
 */
const OtherSessions = ({
  sessions,
  onTerminate,
}: {
  sessions: ActiveSession[]
  onTerminate: (selector: string) => void
}) => {
  const [visibleCount, setVisibleCount] = useState(SESSIONS_PAGE_SIZE)

  const visibleSessions = sessions.slice(0, visibleCount)
  const remainingCount = sessions.length - visibleSessions.length

  return (
    <>
      {visibleSessions.map(({ updatedAt, identity, selector }) => (
        <Fragment key={selector}>
          <SessionItem
            ip={identity?.ip}
            userAgent={identity?.userAgent}
            date={updatedAt}
            onClick={() => onTerminate(selector)}
          />
          <InfixDivider />
        </Fragment>
      ))}

      {remainingCount > 0 && (
        <>
          <Divider />
          <ListItemTextButton
            value={t('activeSessions.showMore')(remainingCount)}
            onClick={() =>
              setVisibleCount(current => current + SESSIONS_PAGE_SIZE)
            }
          />
        </>
      )}
    </>
  )
}
